import { useCallback, useEffect, useState } from "react";

const moduleCache = new Map();

async function loadTemplateModuleData(key, signal) {
  if (!key) return null;
  if (moduleCache.has(key)) return moduleCache.get(key);
  const response = await fetch("/data/modules/" + encodeURIComponent(key) + ".json", {
    signal,
    cache: "no-store",
  });
  if (!response.ok) throw new Error("模块数据读取失败");
  const payload = await response.json();
  moduleCache.set(key, payload);
  return payload;
}

export function useTemplateCatalog() {
  // 目录是各工作台的基础索引，reloadKey 用于在不刷新页面时重新请求。
  const [catalog, setCatalog] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    setError("");

    fetch("/data/catalog.json", { signal: controller.signal, cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error("模板目录读取失败");
        return response.json();
      })
      .then((payload) => {
        setCatalog(payload);
        setStatus("ready");
      })
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        setError(requestError.message || "模板目录读取失败");
        setStatus("error");
      });

    return () => controller.abort();
  }, [reloadKey]);

  return {
    catalog,
    status,
    error,
    retry: () => setReloadKey((value) => value + 1),
  };
}

export function useTemplateModule(moduleKey) {
  // 模块按需加载并在组件生命周期内缓存，减少来回切换产生的重复请求。
  const [moduleData, setModuleData] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const loadModule = useCallback(async (key, signal) => {
    return loadTemplateModuleData(key, signal);
  }, []);

  useEffect(() => {
    if (!moduleKey) {
      setModuleData(null);
      setStatus("idle");
      return undefined;
    }

    const controller = new AbortController();
    setStatus("loading");
    setError("");

    loadModule(moduleKey, controller.signal)
      .then((payload) => {
        setModuleData(payload);
        setStatus("ready");
      })
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        setError(requestError.message || "模块数据读取失败");
        setStatus("error");
      });

    return () => controller.abort();
  }, [loadModule, moduleKey]);

  return { moduleData, status, error };
}

export function useTemplateModules(moduleKeys = []) {
  const [modulesByKey, setModulesByKey] = useState({});
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const stableKeys = moduleKeys.join("|");

  useEffect(() => {
    const keys = stableKeys.split("|").filter(Boolean);
    if (!keys.length) {
      setModulesByKey({});
      setStatus("idle");
      return undefined;
    }

    const controller = new AbortController();
    setStatus("loading");
    setError("");

    Promise.all(keys.map((key) => loadTemplateModuleData(key, controller.signal)))
      .then((modules) => {
        setModulesByKey(Object.fromEntries(modules.filter(Boolean).map((moduleData) => [moduleData.moduleKey, moduleData])));
        setStatus("ready");
      })
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        setError(requestError.message || "模块数据读取失败");
        setStatus("error");
      });

    return () => controller.abort();
  }, [stableKeys]);

  return { modulesByKey, status, error };
}
